// ── StarChart types ─────────────────────────────────────────────────────────
// StarChart is the network popularity/telemetry API at https://popuapi.modded.space.
// It identifies a server by its fully-qualified domain name — the same value ARGUS calls
// `server_address` / `serverAddress` on the PlayCDU server list and `fqdn` on the live status
// socket. The API's own field name for it is `server_id`.
//
// Casing matters upstream: `PROJINF.playcdu.co` and `projinf.playcdu.co` are two genuinely
// distinct tracked series (75 vs 13k snapshots) and both are separately ranked. So ids are sent
// to the API exactly as `/servers` spells them, and lowercased only when joining to the PlayCDU
// server list or the status socket.

/** A server's fully-qualified domain name, as the API spells it. The join key across sources. */
export type ServerFqdn = string;

export type StarchartResolution = 'auto' | 'raw' | '15m' | 'hourly';
/** The resolution the API actually served (never `auto`). */
export type StarchartServedResolution = 'raw' | '15m' | 'hourly';
export type PopularityWindow = '24h' | '7d' | '28d';

export const POPULARITY_WINDOWS: PopularityWindow[] = ['24h', '7d', '28d'];

/** Which series the chart is showing. */
export type StarchartMetric = 'players' | 'tps' | 'mspt' | 'popularity';

/** How the series is transformed before plotting. */
export type NormalizationMode =
  | 'none'
  | 'moving-average'
  | 'min-max'
  | 'z-score'
  | 'percentage-change';

// ── Raw API responses ───────────────────────────────────────────────────────
// Timestamps in every response body are unix **seconds**.

export interface StarchartTimePoint {
  timestamp: number;
  player_count: number;
}

export interface StarchartMsptTimePoint {
  timestamp: number;
  /** Milliseconds per tick. Null when the server was not reporting — never treat as 0. */
  mspt: number | null;
  /** Ticks per second. Null when the server was not reporting — never treat as 0. */
  tps: number | null;
}

export interface StarchartPopularityTimePoint {
  timestamp: number;
  popularity_index: number;
}

export interface TimeSeriesResponse {
  server_id: ServerFqdn;
  start: string;
  end: string;
  resolution: StarchartServedResolution;
  data: StarchartTimePoint[];
}

export interface MsptTimeSeriesResponse {
  server_id: ServerFqdn;
  start: string;
  end: string;
  resolution: StarchartServedResolution;
  data: StarchartMsptTimePoint[];
}

export interface NetworkTimeSeriesResponse {
  start: string;
  end: string;
  resolution: StarchartServedResolution;
  data: StarchartTimePoint[];
}

export interface NetworkMsptTimeSeriesResponse {
  start: string;
  end: string;
  resolution: StarchartServedResolution;
  data: StarchartMsptTimePoint[];
}

export interface NetworkPopularityTimeSeriesResponse {
  window: PopularityWindow;
  start: string;
  end: string;
  data: StarchartPopularityTimePoint[];
}

export interface ServerAggregate {
  server_id: ServerFqdn;
  data: StarchartTimePoint[];
}

export interface RankingItem {
  server_id: ServerFqdn;
  popularity_index: number;
}

export interface RankingsResponse {
  ranked_list: RankingItem[];
}

export interface StarchartSummaryResponse {
  server_id: ServerFqdn;
  start: string;
  end: string;
  average: number;
  peak: number;
  min: number;
  /** Keys observed in the wild: p25, p50, p75. Treated as an open map. */
  percentiles: Record<string, number>;
}

/**
 * Long-run reliability scores. Every field is nullable, and an unknown server comes back
 * HTTP 200 with nulls rather than a 404 — absence of data, not an error.
 */
export interface StarchartServerStats {
  server_id: ServerFqdn;
  avg_players: number | null;
  peak_players: number | null;
  /** 0-100. How steadily the server carries players. */
  consistency_score: number | null;
  /** Lower is better. */
  downtime_score: number | null;
  /** Mean session length contribution, in seconds. */
  session_length_score: number | null;
  snapshot_count: number | null;
  session_count: number | null;
  /** Sum of all session durations, in seconds. */
  total_session_time: number | null;
}

export interface StarchartHealthResponse {
  status: string;
  /**
   * Despite the name this is neither an uptime nor a start time: it is the API's **own current
   * clock** in unix seconds, and `Date.now()/1000 - uptime_seconds` measures 0 (verified across
   * successive calls). It is useful only as a clock-skew check — see `apiClockSkewSeconds()`.
   * The standalone StarChart site rendered it as a duration and therefore always showed "0m".
   */
  uptime_seconds: number;
  /** "YYYY-MM-DD HH:MM:SS", UTC. */
  current_time: string;
  process_id: number;
  active_sessions_count: number;
  redis_keys_count: number;
  db_total_records: number;
}

export interface StarchartDbInfoResponse {
  total_records: number;
  /** Unix seconds. Covers the raw table only — aggregated data reaches further back. */
  earliest_timestamp: number | null;
  latest_timestamp: number | null;
}

export interface StarchartConfigResponse {
  scheduling?: {
    ingest_interval_minutes?: number;
    ranking_interval_minutes?: number;
  };
  retention?: {
    raw_days?: number;
    '15m_months'?: number;
    hourly_years?: number;
  };
}

// ── ARGUS rank history (argus-backend) ──────────────────────────────────────
// Recovers what the upstream cannot serve: per-server popularity and rank over time.

/** Storage granularity: rows compact from 15-minute to hourly to daily as they age. */
export type RankGranularity = '15m' | '1h' | '1d';

export interface RankHistoryPoint {
  /** Unix seconds, floored to the granularity bucket. */
  captured_at: number;
  /** Null when the server was absent from that window's ranking at capture time. */
  popularity_index: number | null;
  /** 1-based position within the window at capture time. */
  rank_position: number | null;
}

export interface RankHistoryCoverage {
  earliest: number | null;
  latest: number | null;
}

export interface RankHistoryResponse {
  server_id: ServerFqdn;
  window: PopularityWindow;
  granularity: RankGranularity;
  start: number;
  end: number;
  coverage: RankHistoryCoverage;
  data: RankHistoryPoint[];
}

export interface RankMovementEntry {
  server_id: ServerFqdn;
  captured_at: number;
  popularity_index: number | null;
  rank_position: number | null;
  /** Null when the server was absent from the baseline snapshot — i.e. it has just appeared. */
  previous_captured_at: number | null;
  previous_popularity_index: number | null;
  previous_rank_position: number | null;
}

export interface RankMovementResponse {
  window: PopularityWindow;
  since_seconds: number;
  coverage: RankHistoryCoverage;
  entries: RankMovementEntry[];
}

// ── Joined view models ──────────────────────────────────────────────────────

/** Live status derived from the status websocket, or null when the server is not on it. */
export interface StarchartLiveStatus {
  playerCount: number;
  maxPlayers: number;
  tps: number;
  mspt: number;
  entities: number;
  chunks: number;
  players: { UUID: string; name: string }[];
  motd: string;
  modloader: string;
  modloaderVersion: string;
  mcVersion: string;
  quasarVersion: string;
  redisPing: number;
  hostname: string;
  identifier: string;
  clusterId: string;
  /** Unix seconds (fractional). */
  lastSeen: number;
  lastBackupTaken: number | null;
  isOffline: boolean;
  isExtendedDowntime: boolean;
  /**
   * Popularity index per window, as carried on the status frame itself. The socket already
   * embeds all three, so a server page gets its current standing without another request.
   */
  popularity: Partial<Record<PopularityWindow, number>>;
}

/** Catalogue metadata from the PlayCDU server list, or null for an archived server. */
export interface StarchartCatalogEntry {
  modpackTitle: string;
  modpackVersion: string;
  modpackAuthor: string | null;
  modpackLauncher: string | null;
  modpackIndexId: number | null;
  minecraftVersion: string | null;
  thumbnailUrl: string | null;
  regionName: string;
  clusterName: string;
  clusterParentServerId: number | null;
  isServerOnline: boolean;
  onlinePlayerCount: number;
  maxPlayerCount: number;
}

/** One row of the network leaderboard: history joined to live status and catalogue metadata. */
export interface StarchartServerRow {
  fqdn: ServerFqdn;
  /** The id exactly as `/servers` spells it — the API is case-sensitive about it. */
  apiServerId: string;
  /** Modpack title when known, else the FQDN. */
  displayName: string;
  catalog: StarchartCatalogEntry | null;
  live: StarchartLiveStatus | null;
  /** Popularity index per window; a window omits servers with no recent activity. */
  popularity: Partial<Record<PopularityWindow, number>>;
  /** 1-based rank per window. */
  rank: Partial<Record<PopularityWindow, number>>;
  /** Positions gained since the comparison snapshot. Positive is an improvement. */
  rankDelta: Partial<Record<PopularityWindow, number>>;
  /** Player counts over the selected range, for the row sparkline. */
  history: StarchartTimePoint[];
  /** Latest player count seen in `history`. Falls back to live when history is empty. */
  latestPlayers: number;
  /** Null when no history was loaded for this server — distinct from a peak of zero. */
  peakPlayers: number | null;
  /** Tracked by StarChart but absent from the PlayCDU server list. */
  isArchived: boolean;
  /** A case-variant of this id exists upstream with far more data — this one is a stub series. */
  isCaseShadowed: boolean;
  /** Present on the server list but not tracked by StarChart — no history exists yet. */
  isUntracked: boolean;
}

/** One modpack cluster's share of the network, for the distribution ring. */
export interface ModpackShareSlice {
  key: string;
  label: string;
  players: number;
  share: number;
  thumbnailUrl: string | null;
  /** Member servers, busiest first. */
  servers: StarchartServerRow[];
}
